import { reactions } from "@/model/card";
import type {
  FieldChange,
  FieldChangeName,
  ReactionField,
  StickyChange,
  StickyChangeKind,
} from "@/model/change";
import { stickyActivityKeys } from "@/model/change";
import { useGlobalI18n } from "@/translations/i18n";
import { valueIn } from "@/utils/record";

import { loadReactiveUser } from "../http/user";
import type {
  ServerFieldChange,
  ServerReactionField,
  ServerStickyChange,
  ServerStickyChangeDelete,
  ServerStickyChangeLink,
  ServerStickyChangeMirror,
  ServerStickyChangeUpdate,
  ServerUser,
} from "../serverModel";
import { mapUserId } from "./mapBackendData";

const { t } = useGlobalI18n();

export function mapStickyChanges(changes: ServerStickyChange[]): StickyChange[] {
  changes.reverse();
  return changes.flatMap((change) => {
    // Guard to ensure the FE only shows changes it knows how to handle.
    // Legacy path only: over NATS the generated response schema is a union of
    // the seven known kinds and is validated with a throwing `parse`, so an
    // unknown kind rejects the whole response before it reaches this guard.
    // Regenerate the stubs before the server starts emitting a new kind.
    const allowedKinds: StickyChangeKind[] = [
      "create",
      "update",
      "delete",
      "mirror",
      "unmirror",
      "link",
      "unlink",
    ];
    if (!allowedKinds.includes(change.kind)) {
      return [];
    }
    const fields = mapChangeFields(change);
    return change.kind === "update" && fields.length === 0
      ? []
      : { ...mapBaseChange(change), fields };
  });
}

function mapBaseChange(change: ServerStickyChange): Omit<StickyChange, "fields"> {
  return {
    stickyId: change.sticky_id,
    timestamp: new Date(change.timestamp),
    user: loadReactiveUser(
      {
        id: change.user_id,
        caller: "history",
        name: t("stickyChange.unknownUser"), // Fallback name if user was deleted
      },
      { mapBackendToAlm: true },
    ),
    kind: change.kind,
  };
}

function mapChangeFields(change: ServerStickyChange): FieldChange[] {
  switch (change.kind) {
    case "create":
      return [];
    case "delete":
      return mapUpdateFields(change);
    case "update":
      return mapUpdateFields(change);
    case "mirror":
      return mapMirrorFields(change);
    case "unmirror":
      return mapUnmirrorFields(change);
    case "link":
      return mapLinkFields(change);
    case "unlink":
      return mapUnlinkFields(change);
  }
}

function mapLinkFields(change: ServerStickyChangeLink): FieldChange[] {
  return [{ name: "link", new: change.linked_to }];
}

function mapUnlinkFields(change: ServerStickyChangeLink): FieldChange[] {
  return [{ name: "link", old: change.linked_to }];
}

function mapMirrorFields(change: ServerStickyChangeMirror): FieldChange[] {
  return [{ name: "mirrorBoard", new: change.to_board }];
}

function mapUnmirrorFields(change: ServerStickyChangeMirror): FieldChange[] {
  return [{ name: "mirrorBoard", old: change.to_board }];
}

function mapUpdateFields(
  change: ServerStickyChangeUpdate | ServerStickyChangeDelete,
): FieldChange[] {
  return (change.changes || []).flatMap((fieldChange) => {
    const key = valueIn(stickyActivityKeys, fieldChange.key);
    switch (key) {
      case undefined:
        return [];
      case "reactions":
        return mapReactionsChange(fieldChange as ServerFieldChange<ServerReactionField>);
      case "teamId":
      case "dependTeamId":
        return mapTeamChange(key, fieldChange as ServerFieldChange<number>);
      case "assignee":
      case "reporter":
        return mapUserChange(key, fieldChange as ServerFieldChange<ChangedUser>);
      default:
        return { name: key, old: fieldChange.old, new: fieldChange.new };
    }
  });
}

function mapReactionsChange(
  fieldChange: ServerFieldChange<ServerReactionField>,
): FieldChange<ReactionField> {
  return {
    name: "reactions",
    old: mapReactions(fieldChange.old),
    new: mapReactions(fieldChange.new),
  };
}

function mapReactions(field: ServerReactionField) {
  return Object.fromEntries(
    reactions.map((reaction) => [reaction, field?.[reaction]?.length || 0]),
  ) as ReactionField;
}

function mapTeamChange(key: FieldChangeName, fieldChange: ServerFieldChange<number>): FieldChange {
  return {
    name: key,
    old: fieldChange.old ? "" + fieldChange.old : undefined,
    new: fieldChange.new ? "" + fieldChange.new : undefined,
  };
}

function mapUserChange(
  key: FieldChangeName,
  fieldChange: ServerFieldChange<ChangedUser>,
): FieldChange {
  return {
    name: key,
    old: mapChangedUser(fieldChange.old),
    new: mapChangedUser(fieldChange.new),
  };
}

/**
 * A user value inside a sticky change. The NATS transport camelCases every
 * nested key of a response, including these opaque field values, so the same
 * payload arrives as pip_id/alm_id over WAMP and as pipId/almId over NATS.
 * Every key is optional: either casing may omit an id it has no value for.
 */
type ChangedUser = Partial<ServerUser & { pipId: string | null; almId: string | null }>;

function mapChangedUser(user?: ChangedUser) {
  if (!user) return undefined;
  return mapUserId(user.pip_id ?? user.pipId, user.alm_id ?? user.almId);
}
